import type { Condition, Signature, TrialResult } from './types';

const CONDITIONS: Condition[] = ['calm', 'signal', 'flow', 'oddball'];

function avg(values: number[]): number {
  if (!values.length) return 1;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function stdev(values: number[]) {
  if (values.length <= 1) return 0;
  const m = avg(values);
  const v = avg(values.map((x) => (x - m) ** 2));
  return Math.sqrt(v);
}

function archetypeFor(values: Record<Condition, number>, elasticity: number): { archetype: string; note: string } {
  const signalLift = values.signal - values.calm;
  const flowDrop = values.flow - values.calm;
  const oddballLift = values.oddball - values.calm;

  if (elasticity < 0.055) {
    return {
      archetype: 'Metronome Mind',
      note: 'Your clock stayed unusually stable across the pressure, flow, and novelty fields.'
    };
  }
  if (flowDrop < -0.12 && signalLift < 0.08) {
    return {
      archetype: 'Tunnel Runner',
      note: 'Attention capture compressed time more than signal pressure stretched it.'
    };
  }
  if (signalLift > 0.12 && flowDrop > -0.05) {
    return {
      archetype: 'Storm Lantern',
      note: 'Urgent signals expanded your felt duration; your inner clock brightens under arousal.'
    };
  }
  if (oddballLift > 0.12) {
    return {
      archetype: 'Novelty Lens',
      note: 'Unexpected events expanded time for you more than the other fields did.'
    };
  }
  if (signalLift > 0.08 && flowDrop < -0.08) {
    return {
      archetype: 'Elastic Switch',
      note: 'Your clock is bidirectional: pressure stretches, flow compresses.'
    };
  }
  return {
    archetype: 'Drift Cartographer',
    note: 'Your pattern is mixed: the shape matters more than one single condition.'
  };
}

export function computeSignature(trials: TrialResult[]): Signature {
  const byCondition = Object.fromEntries(
    CONDITIONS.map((condition) => [
      condition,
      avg(trials.filter((trial) => trial.condition === condition).map((trial) => trial.warpRatio))
    ])
  ) as Record<Condition, number>;

  const ratios = trials.map((trial) => trial.warpRatio);
  const baseline = byCondition.calm;
  const elasticity = stdev(CONDITIONS.map((condition) => byCondition[condition]));
  const precision = clamp(1 - stdev(ratios), 0, 1);
  const kind = archetypeFor(byCondition, elasticity);

  return {
    baseline,
    signal: byCondition.signal,
    flow: byCondition.flow,
    oddball: byCondition.oddball,
    fearStretchIndex: byCondition.signal - baseline,
    flowCompressionIndex: baseline - byCondition.flow,
    noveltyDilationIndex: byCondition.oddball - baseline,
    elasticity,
    precision,
    archetype: kind.archetype,
    note: kind.note,
    trials: trials.length,
    generatedAt: new Date().toISOString()
  };
}

export function formatRatio(ratio: number): string {
  const pct = Math.round((ratio - 1) * 100);
  if (pct === 0) return 'clock-neutral';
  return `${pct > 0 ? '+' : ''}${pct}%`;
}

export function describeRatio(ratio: number): string {
  if (ratio > 1.08) return 'felt longer than clock time';
  if (ratio < 0.92) return 'felt shorter than clock time';
  return 'tracked clock time closely';
}
